// Global banner that listens for the RATE_LIMIT_EVENT emitted by api.ts
// when the backend returns 429. Renders a dismissible amber strip at
// the top of every page with a countdown until retry-after seconds
// expire. Auto-hides when the countdown ends.
//
// Mounted once at the BrowserRouter root in main.tsx so every page
// shows the same banner regardless of where the 429 came from.

import React, { useEffect, useState } from 'react';
import { RATE_LIMIT_EVENT } from '../api';

interface State { retryAfter: number; at: number }

export default function RateLimitBanner(): React.ReactElement | null {
  const [state, setState] = useState<State | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    function onRate(e: Event): void {
      const detail = (e as CustomEvent<State>).detail;
      setState(detail);
    }
    window.addEventListener(RATE_LIMIT_EVENT, onRate);
    return () => window.removeEventListener(RATE_LIMIT_EVENT, onRate);
  }, []);

  useEffect(() => {
    if (!state) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [state]);

  if (!state) return null;
  const elapsed = (now - state.at) / 1000;
  const remaining = Math.max(0, Math.ceil(state.retryAfter - elapsed));
  if (remaining === 0) {
    // Auto-dismiss after the cooldown elapses.
    setTimeout(() => setState(null), 50);
    return null;
  }
  return (
    <div
      role="alert"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999,
        padding: '8px 16px',
        background: 'oklch(0.95 0.06 75)',
        borderBottom: '1px solid oklch(0.78 0.10 75)',
        color: 'oklch(0.32 0.06 70)',
        fontSize: 12.5, fontFamily: 'var(--ui)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M7 4v3l2 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <strong>Rate limited.</strong> Backing off · retry in <code style={{ fontFamily: 'var(--mono)' }}>{remaining}s</code>.
      </span>
      <span style={{ flex: 1 }} />
      <button
        onClick={() => setState(null)}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 16, padding: '0 4px' }}
        aria-label="Dismiss"
      >×</button>
    </div>
  );
}
